import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, BookOpen, FlaskConical, Users, Globe,
  Award, ArrowRight, Calendar, Newspaper, Building2,
  GraduationCap, Trophy, Microscope, MapPin, Phone, Mail, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Layout from '@/components/Layout';
import {
  ParticleCanvas, GlowOrbs, CountUp, TypewriterText, TiltCard, MagneticButton
} from '@/components/AnimatedBackground';

/* ─── data ─────────────────────────────────────────── */
const HERO_SLIDES = [
  { title: '厚德博学 笃行致远', subtitle: '长安大学', desc: '扎根西部，服务国家战略，培育新时代复合型创新人才', accent: '#3b82f6' },
  { title: '世界一流大学建设高校', subtitle: '科技创新 · 人才培养', desc: '国家"双一流"建设高校，工程类学科国内领先', accent: '#22c55e' },
  { title: '百年底蕴 砥砺前行', subtitle: '建校75周年', desc: '传承西北工业精神，引领道路交通与基础设施领域科技进步', accent: '#f59e0b' },
];

const STATS = [
  { label: '全日制在校生', value: 36000, suffix: '+', icon: Users },
  { label: '专任教师', value: 2800, suffix: '+', icon: GraduationCap },
  { label: '本科专业', value: 80, suffix: '个', icon: BookOpen },
  { label: '国家级科研平台', value: 20, suffix: '+', icon: Microscope },
  { label: '院士', value: 12, suffix: '位', icon: Award },
  { label: '国际合作高校', value: 150, suffix: '+', icon: Globe },
];

const NEWS = [
  { tag: '要闻', date: '2026-06-04', title: '我校科研团队在《Nature》子刊发表研究成果，揭示环境健康负担与收入不平等复杂关系' },
  { tag: '学术', date: '2026-06-01', title: '"长安院士专家讲坛"第十讲举行，董勤喜院士作AI+超算仿真赋能交通基础设施报告' },
  { tag: '要闻', date: '2026-06-01', title: '科学技术部科技经费监管服务中心副主任张杨一行来校交流调研' },
  { tag: '校园', date: '2026-06-04', title: '我校斩获RoboMaster 2026机甲大师超级对抗赛北部赛区一等奖' },
  { tag: '要闻', date: '2026-05-22', title: '"人工智能"长安大讲堂举办第13讲，哥伦比亚大学教授主讲' },
  { tag: '校园', date: '2026-05-20', title: '我校学子在第十六届全国大学生市场调查与分析大赛中获佳绩' },
];

const COLLEGES = [
  { name: '公路学院', icon: '🛣️' }, { name: '建筑学院', icon: '🏛️' },
  { name: '信息工程学院', icon: '💻' }, { name: '地质工程与测绘学院', icon: '🗺️' },
  { name: '汽车学院', icon: '🚗' }, { name: '机械学院', icon: '⚙️' },
  { name: '材料科学与工程学院', icon: '🔬' }, { name: '理学院', icon: '🧮' },
  { name: '经济与管理学院', icon: '📊' }, { name: '人文学院', icon: '📚' },
  { name: '外国语学院', icon: '🌐' }, { name: '水利科学与工程学院', icon: '💧' },
];

const QUICK_LINKS = [
  { label: '教务系统', icon: BookOpen }, { label: '图书馆', icon: Building2 },
  { label: '研究生院', icon: GraduationCap }, { label: '就业信息', icon: Trophy },
  { label: '国际合作', icon: Globe }, { label: '科研平台', icon: FlaskConical },
  { label: '校友会', icon: Users }, { label: '校园地图', icon: MapPin },
];

const TAG_COLORS: Record<string, string> = {
  要闻: 'bg-blue-100 text-blue-700',
  学术: 'bg-green-100 text-green-700',
  校园: 'bg-amber-100 text-amber-700',
};

const RESEARCH = [
  { journal: 'Nature · Communications Earth & Environment', title: '揭示空气质量改善背景下环境健康负担与收入不平等之间的复杂关系', authors: '刘明副教授（第一作者），长安大学（第一完成单位）', date: '2026-06-04' },
  { journal: 'National Science Review', title: '中国"山水工程"修复成效评价研究取得新进展', authors: '申艳军教授，彭建兵院士（通讯作者）', date: '2026-05-10' },
  { journal: 'Science China · Earth Science', title: '青藏湖泊自末次冰盛期以来持续作为CO₂源的研究成果', authors: '刘虎副教授（第一作者），旱区地下水文与生态效应教育部重点实验室', date: '2026-04-18' },
  { journal: 'Tectonophysics', title: '在读博士生在国际知名地球物理期刊发表重要研究成果', authors: '长安大学在读博士研究生为第一作者', date: '2026-03-22' },
];

/* ─── inline keyframes ─── */
const STYLE = `
@keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
@keyframes borderSpin { from{--a:0deg} to{--a:360deg} }
@keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
@keyframes pulse-glow { 0%,100%{box-shadow:0 0 0 0 rgba(59,130,246,0)} 50%{box-shadow:0 0 24px 6px rgba(59,130,246,0.3)} }
@keyframes slide-up { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
@keyframes wave { 0%{transform:scaleX(1) scaleY(1)} 50%{transform:scaleX(1.05) scaleY(0.97)} 100%{transform:scaleX(1) scaleY(1)} }
.stat-card:hover .stat-icon { animation: float 2s ease-in-out infinite; }
.news-card:hover { box-shadow: 0 0 0 2px rgba(59,130,246,0.4), 0 16px 40px rgba(59,130,246,0.12); }
.college-card:hover .college-emoji { animation: float 1.5s ease-in-out infinite; }
.shimmer-text { background: linear-gradient(90deg,#fff 0%,rgba(255,255,255,0.5) 40%,#fff 60%,rgba(255,255,255,0.5) 100%); background-size:200% auto; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation: shimmer 3s linear infinite; }
.glow-border { position:relative; }
.glow-border::before { content:''; position:absolute; inset:-1.5px; border-radius:inherit; background:linear-gradient(135deg,rgba(99,155,255,0.7),rgba(168,85,247,0.5),rgba(99,155,255,0.7)); opacity:0; transition:opacity 0.3s; z-index:0; }
.glow-border:hover::before { opacity:1; }
.glow-border > * { position:relative; z-index:1; }
`;

/* ─── component ────────────────────────────────────── */
export default function HomePage() {
  const [slide, setSlide] = useState(0);
  const [hoveredNews, setHoveredNews] = useState<number | null>(null);
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0, 500], [0, -120]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.3]);
  const springParallax = useSpring(heroParallax, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const cur = HERO_SLIDES[slide];

  return (
    <Layout>
      <style>{STYLE}</style>

      {/* @section: hero */}
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: '560px', background: 'linear-gradient(135deg,#000d1f 0%,#001a4d 40%,#003087 80%,#00408a 100%)' }}>
        <ParticleCanvas count={80} color="rgba(180,210,255,0.6)" />
        <GlowOrbs orbs={[
          { color: 'radial-gradient(circle,rgba(59,130,246,0.8),transparent)', size: 500, x: '15%', y: '20%', delay: 0 },
          { color: 'radial-gradient(circle,rgba(139,92,246,0.7),transparent)', size: 400, x: '80%', y: '60%', delay: 2 },
          { color: `radial-gradient(circle,${cur.accent}80,transparent)`, size: 350, x: '55%', y: '10%', delay: 1 },
        ]} />
        {/* Grid overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <motion.div className="relative max-w-7xl mx-auto px-6 py-20 flex flex-col lg:flex-row items-center gap-12" style={{ y: springParallax, opacity: heroOpacity }}>
          <AnimatePresence mode="wait">
            <motion.div key={slide}
              initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, x: 50, filter: 'blur(10px)' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="flex-1 text-white">
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Badge className="mb-5 border border-white/20 text-white text-xs px-3 py-1" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}>
                  <Sparkles className="w-3 h-3 mr-1.5 inline" />{cur.subtitle}
                </Badge>
              </motion.div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-5 leading-tight" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                <TypewriterText key={slide} text={cur.title} className="shimmer-text" speed={60} />
              </h1>
              <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="text-white/70 text-lg mb-9 max-w-xl leading-relaxed">{cur.desc}</motion.p>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="flex flex-wrap gap-3">
                <MagneticButton>
                  <motion.button whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/auth')}
                    className="px-7 py-2.5 rounded-xl font-semibold text-sm"
                    style={{ background: 'rgba(255,255,255,0.95)', color: '#003087', boxShadow: '0 4px 24px rgba(59,130,246,0.35)' }}>
                    登录校园门户
                  </motion.button>
                </MagneticButton>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className="px-7 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 text-white"
                  style={{ border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}>
                  了解长安大学 <ArrowRight className="w-4 h-4" />
                </motion.button>
              </motion.div>
              {/* Slide dots */}
              <div className="flex gap-2 mt-9">
                {HERO_SLIDES.map((_, i) => (
                  <motion.button key={i} onClick={() => setSlide(i)} animate={{ width: i === slide ? 32 : 12, opacity: i === slide ? 1 : 0.4 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="h-1.5 rounded-full bg-white" />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Hero card */}
          <motion.div
            initial={{ opacity: 0, x: 60, rotateY: -15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="w-full lg:w-80 rounded-2xl p-5 border border-white/10"
            style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(20px)' }}>
            <div className="flex items-center gap-2 mb-4">
              <motion.div animate={{ rotate: [0, 20, -20, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}>
                <Newspaper className="w-4 h-4 text-blue-300" />
              </motion.div>
              <span className="text-white/90 text-sm font-medium">最新资讯</span>
              <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="w-2 h-2 rounded-full bg-green-400 ml-auto" />
            </div>
            <div className="space-y-3">
              {NEWS.slice(0, 4).map((n, i) => (
                <motion.a key={i} href="#"
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                  whileHover={{ x: 4 }}
                  className="flex items-start gap-2 group">
                  <span className="shrink-0 mt-0.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-white/15 text-white/90 border border-white/10">{n.tag}</span>
                  <p className="text-white/70 text-xs leading-relaxed group-hover:text-white transition-colors duration-200 line-clamp-2">{n.title}</p>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none" style={{ background: 'linear-gradient(to top, white, transparent)' }} />
      </section>

      {/* @section: stats */}
      <section className="bg-white relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <motion.div className="grid grid-cols-3 md:grid-cols-6 gap-2"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}>
            {STATS.map((s) => (
              <motion.div key={s.label}
                variants={{ hidden: { opacity: 0, y: 30, scale: 0.8 }, visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 400, damping: 25 } } }}
                className="stat-card text-center p-4 rounded-2xl cursor-pointer group glow-border"
                style={{ background: 'white', border: '1px solid rgba(0,48,135,0.08)' }}
                whileHover={{ y: -4 }}>
                <div className="stat-icon w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,rgba(0,48,135,0.12),rgba(59,130,246,0.12))' }}>
                  <s.icon className="w-4 h-4 text-primary" />
                </div>
                <div className="text-2xl font-bold text-primary" style={{ fontFamily: 'var(--font-mono)' }}>
                  <CountUp target={s.value} />{s.suffix}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* @section: quick-links */}
      <section className="py-8" style={{ background: 'linear-gradient(135deg,#f0f6ff,#f8faff)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <motion.div className="grid grid-cols-4 md:grid-cols-8 gap-3"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}>
            {QUICK_LINKS.map((q) => (
              <motion.div key={q.label}
                variants={{ hidden: { opacity: 0, scale: 0.7, y: 20 }, visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 500, damping: 28 } } }}>
                <MagneticButton className="w-full">
                  <motion.a href="#" whileHover={{ scale: 1.08, y: -3 }} whileTap={{ scale: 0.94 }}
                    className="flex flex-col items-center gap-2 p-3 bg-white rounded-2xl transition-all group cursor-pointer border border-border/60"
                    style={{ boxShadow: '0 2px 12px rgba(0,48,135,0.05)' }}>
                    <motion.div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,rgba(0,48,135,0.1),rgba(59,130,246,0.1))' }}
                      whileHover={{ rotate: [0, -8, 8, 0], transition: { duration: 0.4 } }}>
                      <q.icon className="w-5 h-5 text-primary" />
                    </motion.div>
                    <span className="text-xs text-center font-medium text-foreground">{q.label}</span>
                  </motion.a>
                </MagneticButton>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* @section: news-grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div className="flex items-center justify-between mb-10"
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Noto Serif SC', serif", color: 'oklch(0.20 0.10 240)' }}>新闻动态</h2>
              <motion.div className="h-1 rounded-full mt-2 bg-primary origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} style={{ width: 48 }} />
            </div>
            <motion.a href="#" whileHover={{ x: 4 }} className="flex items-center gap-1 text-sm text-primary hover:underline">
              查看更多 <ChevronRight className="w-4 h-4" />
            </motion.a>
          </motion.div>
          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.09 } } }}>
            {NEWS.map((n, i) => (
              <motion.article key={i}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 350, damping: 28 } } }}
                className="news-card group bg-white border border-border rounded-2xl p-5 cursor-pointer transition-all duration-300"
                onHoverStart={() => setHoveredNews(i)}
                onHoverEnd={() => setHoveredNews(null)}
                whileHover={{ y: -6 }}>
                <div className="flex items-center justify-between mb-3">
                  <Badge className={`text-xs font-medium border-0 ${TAG_COLORS[n.tag]}`}>{n.tag}</Badge>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="w-3 h-3" />{n.date}</div>
                </div>
                <h3 className="text-sm font-medium leading-relaxed group-hover:text-primary transition-colors line-clamp-3">{n.title}</h3>
                <AnimatePresence>
                  {hoveredNews === i && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                      className="flex items-center gap-1 mt-3 text-xs text-primary font-medium">
                      阅读全文 <ArrowRight className="w-3 h-3" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* @section: colleges */}
      <section className="py-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#f0f6ff 0%,#f8f0ff 100%)' }}>
        <GlowOrbs orbs={[
          { color: 'radial-gradient(circle,rgba(59,130,246,0.15),transparent)', size: 600, x: '90%', y: '50%', delay: 0 },
          { color: 'radial-gradient(circle,rgba(139,92,246,0.12),transparent)', size: 400, x: '5%', y: '30%', delay: 2 },
        ]} />
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div className="mb-10" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Noto Serif SC', serif", color: 'oklch(0.20 0.10 240)' }}>学院风采</h2>
            <motion.div className="h-1 rounded-full mt-2 bg-primary origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} style={{ width: 48 }} />
          </motion.div>
          <motion.div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}>
            {COLLEGES.map((c, i) => (
              <motion.a key={c.name} href="#"
                variants={{ hidden: { opacity: 0, scale: 0.6, rotate: -8 }, visible: { opacity: 1, scale: 1, rotate: 0, transition: { type: 'spring', stiffness: 450, damping: 22, delay: i * 0.03 } } }}
                className="college-card flex flex-col items-center gap-2.5 p-4 bg-white/80 rounded-2xl border border-white cursor-pointer glow-border"
                style={{ backdropFilter: 'blur(8px)', boxShadow: '0 2px 16px rgba(0,48,135,0.06)' }}
                whileHover={{ y: -5, scale: 1.05 }}>
                <span className="college-emoji text-2xl">{c.icon}</span>
                <span className="text-xs font-medium leading-tight group-hover:text-primary transition-colors text-center">{c.name}</span>
              </motion.a>
            ))}
            <motion.a href="#"
              variants={{ hidden: { opacity: 0, scale: 0.6 }, visible: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 450, damping: 22, delay: 0.5 } } }}
              className="flex flex-col items-center gap-2.5 p-4 rounded-2xl border cursor-pointer text-center"
              style={{ background: 'linear-gradient(135deg,rgba(0,48,135,0.06),rgba(59,130,246,0.06))', borderColor: 'rgba(0,48,135,0.15)' }}
              whileHover={{ y: -5, scale: 1.05 }}>
              <span className="text-2xl">🏫</span>
              <span className="text-xs font-medium text-primary">查看全部</span>
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* @section: research */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div className="mb-10" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Noto Serif SC', serif", color: 'oklch(0.20 0.10 240)' }}>科研成果</h2>
            <motion.div className="h-1 rounded-full mt-2 bg-primary origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} style={{ width: 48 }} />
          </motion.div>
          <motion.div className="grid md:grid-cols-2 gap-5"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}>
            {RESEARCH.map((r, i) => (
              <TiltCard key={i} intensity={5}
                className="flex gap-4 p-5 border border-border rounded-2xl cursor-pointer bg-white glow-border"
                style={{ boxShadow: '0 2px 16px rgba(0,48,135,0.04)' } as React.CSSProperties}>
                <motion.div className="shrink-0 w-1 rounded-full self-stretch bg-primary origin-top"
                  initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 + 0.2 }} />
                <div className="flex-1">
                  <Badge className="mb-2 text-[10px] border-0 font-medium bg-primary/10 text-primary">{r.journal}</Badge>
                  <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors mb-1.5 leading-relaxed">{r.title}</h3>
                  <p className="text-xs text-muted-foreground">{r.authors}</p>
                  <p className="text-xs text-muted-foreground mt-1">{r.date}</p>
                </div>
              </TiltCard>
            ))}
          </motion.div>
        </div>
      </section>

      {/* @section: cta */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#000d1f,#001a4d,#003087)' }}>
        <ParticleCanvas count={50} color="rgba(180,210,255,0.4)" />
        <GlowOrbs orbs={[
          { color: 'radial-gradient(circle,rgba(59,130,246,0.5),transparent)', size: 600, x: '20%', y: '50%', delay: 0 },
          { color: 'radial-gradient(circle,rgba(139,92,246,0.4),transparent)', size: 450, x: '80%', y: '50%', delay: 1.5 },
        ]} />
        <div className="relative max-w-7xl mx-auto px-6 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 200, damping: 30 }}>
            <motion.div animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 4, repeat: Infinity }}>
              <Sparkles className="w-8 h-8 mx-auto mb-4 text-blue-300" />
            </motion.div>
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              <span className="shimmer-text">欢迎加入长安大学</span>
            </h2>
            <p className="text-white/60 mb-10 max-w-xl mx-auto text-lg">登录校园门户，获取学术资源、教务信息和校园服务</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <MagneticButton>
                <motion.button whileHover={{ scale: 1.07, boxShadow: '0 0 40px rgba(255,255,255,0.3)' }} whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/auth')}
                  className="px-10 py-3 rounded-2xl font-semibold text-primary"
                  style={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 4px 24px rgba(59,130,246,0.4)' }}>
                  登录 / 注册
                </motion.button>
              </MagneticButton>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="px-10 py-3 rounded-2xl font-semibold text-white"
                style={{ border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(8px)' }}>
                本科招生信息
              </motion.button>
            </div>
            <motion.div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-white/50"
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}>
              {[
                { icon: MapPin, text: '陕西省西安市南二环西段' },
                { icon: Phone, text: '029-82334114' },
                { icon: Mail, text: 'webmaster@chd.edu.cn' },
              ].map(({ icon: Icon, text }) => (
                <motion.div key={text} variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
                  className="flex items-center gap-2 hover:text-white/80 transition-colors cursor-pointer">
                  <Icon className="w-4 h-4" />{text}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
